import { partition } from 'lodash'
import { useMemo, type ReactNode, type ReactElement, Children, isValidElement } from 'react'
import Box from '@mui/material/Box'
import { DEFAULT_SEVERITY, type BannerProps } from '@ui/features/banners/Banner'

type StackBannersProps = { children: ReactNode }

const SEVERITY_ORDER: Record<NonNullable<BannerProps['severity']>, number> = {
  alert: 0,
  warning: 1,
  caution: 2,
  highlight: 3,
  info: 4,
}

const compareBanners = (a: ReactElement<BannerProps>, b: ReactElement<BannerProps>) =>
  SEVERITY_ORDER[a.props.severity || DEFAULT_SEVERITY] - SEVERITY_ORDER[b.props.severity || DEFAULT_SEVERITY]

/**
 * Renders a grouped and sorted list of banners.
 * It separates non-removable banners (without onClick) from removable ones.
 * It renders non-removable first, then removable. Within each group, banners are sorted by severity.
 */
export const StackBanners = ({ children }: StackBannersProps) => {
  const sortedBanners = useMemo(() => {
    // eslint-disable-next-line @eslint-react/no-children-to-array -- Existing violation before enabling this rule.
    const banners = Children.toArray(children).filter(isValidElement) as ReactElement<BannerProps>[]
    const [removable, nonRemovable] = partition(banners, el => !!el.props.onClick)
    // eslint-disable-next-line local/no-mutable-array-methods -- Existing violation before creating this rule.
    return [...nonRemovable.sort(compareBanners), ...removable.sort(compareBanners)]
  }, [children])

  return <Box>{sortedBanners}</Box>
}
